import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { createSocket } from "@/lib/socket";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { PaperPlaneTilt, ChatCircleText, Storefront } from "@phosphor-icons/react";

export default function ChatRoom({ auctionId, sellerId }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);
  const socketRef = useRef(null);
  const [onlineHint, setOnlineHint] = useState(false);

  // Load history
  useEffect(() => {
    api.get(`/auctions/${auctionId}/chat`).then(({ data }) => setMessages(data)).catch(() => {});
  }, [auctionId]);

  // Socket
  useEffect(() => {
    const s = createSocket();
    socketRef.current = s;
    s.on("connect", () => {
      s.emit("join_chat", { auction_id: auctionId });
      setOnlineHint(true);
    });
    s.on("chat_message", (msg) => {
      if (msg.auction_id !== auctionId) return;
      setMessages((prev) => [...prev, msg]);
    });
    s.on("disconnect", () => setOnlineHint(false));
    return () => {
      try { s.emit("leave_chat", { auction_id: auctionId }); } catch (e) { /* ignore */ }
      if (s.connected) s.disconnect();
    };
  }, [auctionId]);

  // Auto-scroll on new message
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [messages]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    if (!user) return toast.error("Sohbet için giriş yapmanız gerekiyor");
    setSending(true);
    try {
      await api.post(`/auctions/${auctionId}/chat`, { text: text.trim() });
      setText("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Mesaj gönderilemedi");
    } finally {
      setSending(false);
    }
  };

  return (
    <section className="mt-24 pt-14 border-t divider" data-testid="chat-room">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-2">
        <div>
          <p className="text-[11px] uppercase tracking-[0.28em] font-medium text-slate-400 mb-2">Canlı sohbet</p>
          <h2 className="font-display text-2xl font-medium tracking-tight text-slate-900 flex items-center gap-2">
            <ChatCircleText size={20} weight="regular" /> İlan sohbet odası
          </h2>
        </div>
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.24em] font-medium">
          <span className={`inline-block w-1.5 h-1.5 rounded-full ${onlineHint ? "bg-emerald-500" : "bg-slate-300"}`} />
          <span className={onlineHint ? "text-emerald-800" : "text-slate-400"}>{onlineHint ? "Bağlı" : "Bağlantı yok"}</span>
        </div>
      </div>

      <div className="rounded-2xl overflow-hidden flex flex-col bg-slate-50" style={{ maxHeight: 500 }}>
        <div className="flex-1 overflow-y-auto p-5 space-y-3 min-h-[240px]" data-testid="chat-messages">
          {messages.length === 0 && (
            <p className="text-center text-sm text-slate-400 py-10">Henüz mesaj yok. İlk mesajı sen yaz.</p>
          )}
          {messages.map((m) => {
            const mine = user?.user_id === m.user_id;
            const seller = m.user_id === sellerId;
            return (
              <div key={m.id} className={`flex gap-2 ${mine ? "justify-end" : "justify-start"}`}>
                {!mine && (
                  <div className="w-7 h-7 rounded-full bg-slate-900 text-white text-[11px] font-medium flex items-center justify-center flex-none">
                    {m.user_picture ? <img src={m.user_picture} alt="" className="w-full h-full rounded-full object-cover" /> : (m.user_name?.[0] || "?")}
                  </div>
                )}
                <div className={`max-w-[70%] rounded-2xl px-4 py-2.5 ${
                  mine
                    ? "bg-slate-900 text-white rounded-br-md"
                    : seller
                      ? "bg-white text-slate-900 rounded-bl-md ring-1 ring-amber-200"
                      : "bg-white text-slate-900 rounded-bl-md"
                }`}>
                  {!mine && (
                    <p className={`text-[10px] uppercase tracking-[0.22em] font-medium mb-1 flex items-center gap-1 ${seller ? "text-amber-700" : "text-slate-400"}`}>
                      {seller && <Storefront size={10} weight="fill" />}
                      {m.user_name} {seller && "· Satıcı"}
                    </p>
                  )}
                  <p className="text-sm leading-snug break-words">{m.text}</p>
                  <p className={`text-[10px] mt-1 font-mono-tight ${mine ? "text-white/60" : "text-slate-400"}`}>
                    {new Date(m.timestamp).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" })}
                  </p>
                </div>
              </div>
            );
          })}
          <div ref={bottomRef} />
        </div>

        <form onSubmit={send} className="border-t divider bg-white p-3 flex gap-2">
          <input
            data-testid="chat-input"
            value={text}
            onChange={(e) => setText(e.target.value)}
            disabled={!user || sending}
            placeholder={user ? "Mesajınızı yazın… (telefon/e-posta yasak)" : "Sohbet için giriş yapın"}
            className="flex-1 bg-slate-50 border border-slate-200 rounded-lg px-4 py-2.5 focus:outline-none focus:border-slate-900 focus:bg-white text-sm disabled:opacity-60 transition-colors"
            maxLength={500}
          />
          <button
            type="submit"
            disabled={!user || sending || !text.trim()}
            data-testid="chat-send"
            className="bg-slate-900 text-white px-5 py-2.5 rounded-lg font-medium hover:bg-slate-800 transition-colors disabled:opacity-60 flex items-center gap-1.5"
          >
            <PaperPlaneTilt size={15} weight="fill" /> Gönder
          </button>
        </form>
      </div>
    </section>
  );
}
